import React, { useRef, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FormControl, TextField } from "@mui/material";
import { clsx } from "@ismael1361/utils";

interface OTPInputProps {
	value: string[];
	onChange: (value: string[]) => void;
	disabled?: boolean;
	separator?: boolean;
}

type CSSProperties = React.CSSProperties & {
	[k in `--${string}`]: string | number | CSSProperties;
};

export const OTPInput: React.FC<OTPInputProps> = ({ value, onChange, disabled, separator }) => {
	const [values, setValues] = useState<string[]>(value);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (value.join("") === inputRefs.current.map((e) => e?.value || "").join("")) return;
		setValues(value);
	}, [value]);

	useEffect(() => {
		if (inputRefs.current[0]) {
			inputRefs.current[0].focus();
		}
	}, []);

	const handleFocus = (index: number) => (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
	};

	const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;

		if (val.length > 1) {
			const pasteValues = val.split("").filter((char) => /^[a-zA-Z0-9]$/.test(char));
			if (pasteValues.length === 0) return;

			const newOtp = [...values];
			for (let i = 0; i < pasteValues.length; i++) {
				if (index + i < newOtp.length) {
					newOtp[index + i] = pasteValues[i];
				}
			}

			setValues(newOtp);
			onChange(newOtp);

			const nextIndex = Math.min(index + pasteValues.length, newOtp.length - 1);
			inputRefs.current[nextIndex]?.focus();
			return;
		}

		const targetValue = val.slice(-1);

		if (!/^[a-zA-Z0-9]$/.test(targetValue)) {
			return;
		}

		const newOtp = [...values];
		newOtp[index] = targetValue;
		setValues(newOtp);
		onChange(newOtp);

		if (targetValue && index < values.length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace") {
			if (values[index] === "" && index > 0) {
				inputRefs.current[index - 1]?.focus();
			} else {
				const newOtp = [...values];
				newOtp[index] = "";
				setValues(newOtp);
				onChange(newOtp);
			}
		}
	};

	const colsDesktop = values.length <= 8 ? values.length : values.length % 8 === 0 ? 8 : values.length % 6 === 0 ? 6 : 8;
	const colsMobile = values.length <= 4 ? values.length : values.length % 4 === 0 ? 4 : values.length % 3 === 0 ? 3 : 4;

	return (
		<div
			className={styles.otpContainer}
			style={{
				"--cols-desktop": colsDesktop,
				"--cols-mobile": colsMobile,
			}}
		>
			{values.map((data, index) => (
				<React.Fragment key={index}>
					<TextField
						className={clsx(styles.otpInput, data && styles.active)}
						inputRef={(el) => {
							inputRefs.current[index] = el;
						}}
						variant="outlined"
						type="text"
						value={data}
						onChange={handleChange(index)}
						onFocus={handleFocus(index)}
						onKeyDown={handleKeyDown(index)}
						disabled={disabled}
						autoComplete="off"
					/>
					{separator && index === Math.floor(values.length / 2) - 1 ? <span className={styles.separator}> </span> : null}
				</React.Fragment>
			))}
		</div>
	);
};
